import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  InputSearch,
  TableData,
  Pagination,
  UniversalFilter,
  ResetFiltersButton,
  DateRangeFilter,
  Status,
  ActionDropdown,
  ActionDropdownAction,
  ColumnSettings,
} from '@/components';
import {
  useListQuery,
  useNavigateFromList,
  usePageClamp,
} from '@/hooks/useUrlListState';
import { useTableColumns } from '@/hooks/useTableColumns';
import dayjs from 'dayjs';
import {
  Column,
  Handover,
  HandoverStatus,
  BUSINESS_UNIT_OPTIONS,
  getBusinessUnitLabel,
} from '@/types/common';
import { getListHandover } from '@/services/api/handovers';
import { copyToClipboard, extractApiErrorMessage } from '@/utils';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import {
  HANDOVER_FILTER_KEYS,
  HANDOVER_LIST_DEFAULTS,
} from './handoverListQuery';

interface HistoryHandoverTabProps {
  onEditHandover: (handover: Handover) => void;
  refreshTrigger: number;
  vehicles: any[];
  clients: any[];
  pitstops: any[];
  hubLocations: any[];
  oems: any[];
  models: any[];
  onTotalChange?: (total: number) => void; // New prop to communicate total back to parent
  onDownloadParamsChange?: (params: Record<string, any>) => void;
}

const HistoryHandoverTab: React.FC<HistoryHandoverTabProps> = ({
  onEditHandover,
  refreshTrigger,
  vehicles,
  clients,
  pitstops,
  hubLocations,
  oems,
  models,
  onTotalChange,
  onDownloadParamsChange,
}) => {
  const {
    searchTerm,
    currentPage,
    pageSize,
    sortField,
    sortDirection,
    activeFilters,
    dateRange,
    datePickerSeed,
    controlsKey,
    searchInput,
    setSearchInput,
    setSearchTerm,
    setCurrentPage,
    setFilters,
    setDateRange,
    setSort,
    setPageSize,
    hasFilters,
    resetFilters,
    clampToTotal,
  } = useListQuery(HANDOVER_LIST_DEFAULTS, HANDOVER_FILTER_KEYS);
  const [handoverData, setHandoverData] = useState<Handover[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);
  const navigate = useNavigateFromList();

  const handleToggleDropdown = useCallback((id: string | number) => {
    setActionDropdownOpen((prev) => (prev === id ? null : id));
  }, []);

  useEffect(() => {
    const handleOutsideClick = () => setActionDropdownOpen(null);
    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleOutsideClick);
    }
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [actionDropdownOpen]);

  // Status label mapper
  const statusLabelMapper: Record<HandoverStatus, string> = {
    [HandoverStatus.ACTIVE]: 'Active',
    [HandoverStatus.RETURNED]: 'Returned',
    [HandoverStatus.PRE_RETURN]: 'Pre-Return',
  };

  // Filter configuration for UniversalFilter
  const filterConfigs = useMemo(
    () => [
      {
        key: 'oemIDs',
        title: 'OEM',
        icon: 'ri-building-line',
        multiple: true,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'modelIDs',
        title: 'Models',
        icon: 'ri-car-line',
        multiple: true,
        dataSource: models,
        labelFormatter: (model: any) => `${model?.name} - ${model?.oemName}`,
        valueField: 'ID',
      },
      {
        key: 'clientIDs',
        title: 'Client',
        icon: 'ri-user-line',
        multiple: true,
        dataSource: clients,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'hubIDs',
        title: 'Pitstop',
        icon: 'ri-store-2-line',
        multiple: true,
        dataSource: pitstops,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'vehicleHubIDs',
        title: 'Hub Location',
        icon: 'ri-building-line',
        multiple: true,
        dataSource: hubLocations,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'businessUnits',
        title: 'Business Unit',
        icon: 'ri-briefcase-line',
        multiple: true,
        staticOptions: BUSINESS_UNIT_OPTIONS,
      },
    ],
    [oems, models, clients, pitstops, hubLocations]
  );

  const fetchHandovers = useCallback(
    async (
      page: number = 1,
      search?: string,
      filters?: Record<string, any>,
      size?: number,
      range?: { from: Date | null; to: Date | null }
    ) => {
      setIsLoading(true);
      try {
        const params: any = {
          page: page.toString(),
          size: (size || pageSize).toString(),
          statuses: HandoverStatus.RETURNED,
          order: 'desc',
          sort: 'returnDate',
        };

        if (search?.trim()) {
          params.search = search.trim();
        }

        // Add other filters
        if (filters?.oemIDs && filters.oemIDs.length > 0) {
          params.oemIDs = filters.oemIDs.join(',');
        }
        if (filters?.modelIDs && filters.modelIDs.length > 0) {
          params.modelIDs = filters.modelIDs.join(',');
        }
        if (filters?.clientIDs && filters.clientIDs.length > 0) {
          params.clientIDs = filters.clientIDs.join(',');
        }
        if (filters?.hubIDs && filters.hubIDs.length > 0) {
          params.hubIDs = filters.hubIDs.join(',');
        }
        if (filters?.vehicleHubIDs && filters.vehicleHubIDs.length > 0) {
          params.vehicleHubIDs = filters.vehicleHubIDs.join(',');
        }
        if (filters?.businessUnits && filters.businessUnits.length > 0) {
          params.businessUnits = filters.businessUnits.join(',');
        }

        if (range?.from) {
          const d = range.from;
          params.returnStartDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (range?.to) {
          const d = range.to;
          params.returnEndDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }

        const response = await getListHandover(params);

        setHandoverData(response.data || []);
        const totalCount = response.pagination?.total || 0;
        setTotal(totalCount);
        onTotalChange?.(totalCount); // Notify parent of total change

        if (response.pagination?.page) {
          setCurrentPage(response.pagination.page);
        }
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to load handover history'
        );
        showErrorToast('Error', errorMessage);
        setHandoverData([]);
        setTotal(0);
        onTotalChange?.(0); // Notify parent that total is 0
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize, onTotalChange, setCurrentPage]
  );

  useEffect(() => {
    fetchHandovers(currentPage, searchTerm, activeFilters, pageSize, dateRange);
  }, [
    fetchHandovers,
    currentPage,
    searchTerm,
    activeFilters,
    refreshTrigger,
    pageSize,
    dateRange,
  ]);

  // A page restored from the URL can sit past the end once the list shrinks.
  usePageClamp(clampToTotal, total, isLoading);

  useEffect(() => {
    if (!onDownloadParamsChange) return;

    const params: Record<string, any> = {
      statuses: HandoverStatus.RETURNED,
    };

    if (activeFilters?.oemIDs && activeFilters.oemIDs.length > 0) {
      params.oemIDs = activeFilters.oemIDs.join(',');
    }
    if (activeFilters?.modelIDs && activeFilters.modelIDs.length > 0) {
      params.modelIDs = activeFilters.modelIDs.join(',');
    }
    if (activeFilters?.clientIDs && activeFilters.clientIDs.length > 0) {
      params.clientIDs = activeFilters.clientIDs.join(',');
    }
    if (activeFilters?.hubIDs && activeFilters.hubIDs.length > 0) {
      params.hubIDs = activeFilters.hubIDs.join(',');
    }
    if (
      activeFilters?.vehicleHubIDs &&
      activeFilters.vehicleHubIDs.length > 0
    ) {
      params.vehicleHubIDs = activeFilters.vehicleHubIDs.join(',');
    }
    if (
      activeFilters?.businessUnits &&
      activeFilters.businessUnits.length > 0
    ) {
      params.businessUnits = activeFilters.businessUnits.join(',');
    }

    if (dateRange?.from) {
      const d = dateRange.from;
      params.returnStartDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    if (dateRange?.to) {
      const d = dateRange.to;
      params.returnEndDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }

    onDownloadParamsChange(params);
  }, [
    onDownloadParamsChange,
    currentPage,
    pageSize,
    searchTerm,
    activeFilters,
    dateRange,
  ]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Client-side sorting
  const sortedHandoverData = useMemo(() => {
    if (!sortField || !sortDirection) {
      return handoverData;
    }

    return [...handoverData].sort((a: any, b: any) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue === bValue) return 0;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      } else {
        comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [handoverData, sortField, sortDirection]);

  const columns: Column[] = [
    {
      key: 'platNomor',
      title: 'Vehicle',
      width: 'w-32',
      render: (value: string, record: Handover) => (
        <div>
          <span className="text-[14px] font-medium">{value ? value : '-'}</span>
          <br />
          <span className="text-[12px] text-gray-500">
            {record.oemName + ' - ' + record.modelName}
          </span>
        </div>
      ),
    },

    {
      key: 'driverName',
      title: 'Mitra Name',
      width: 'w-32',
      render: (value: string, record: Handover) => (
        <div
          className={`text-[14px] ${
            record.driverID
              ? 'text-underline text-blue-600 cursor-pointer hover:text-blue-800'
              : 'text-gray-900 cursor-default'
          }`}
          onClick={(e) => {
            if (record.driverID) {
              e.stopPropagation();
              const backOfficeUrl =
                process.env.REACT_APP_BACK_OFFICE_URL ||
                'https://back-office.dashelectric.co';
              window.open(
                `${backOfficeUrl}/driver/${record.driverID}`,
                '_blank'
              );
            }
          }}
        >
          {value ? (
            <>
              <span className="font-medium">{value}</span>
              <br />
              <span className="text-[12px] text-gray-500">
                {record.driverCode}
              </span>
            </>
          ) : (
            <div>-</div>
          )}
        </div>
      ),
    },
    {
      key: 'vehicleHubName',
      title: 'Hub Location',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">{value ? value : '-'}</span>
      ),
    },
    {
      key: 'vehicleBusinessUnit',
      title: 'Business Unit',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? (
            getBusinessUnitLabel(value)
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'clientName',
      title: 'Client',
      width: 'w-32',
      render: (value: string) => (
        <span className="text-[14px] font-normal">{value ? value : '-'}</span>
      ),
    },
    {
      key: 'hubName',
      title: 'Pitstop',
      width: 'w-40',
      render: (value: string) => (
        <span className="text-[14px] font-normal">{value ? value : '-'}</span>
      ),
    },
    {
      key: 'handoverDate',
      title: 'Handover Date',
      align: 'center',
      width: 'w-32',
      render: (value: string) => (
        <span className="text-[14px]">
          {value ? dayjs(value).format('DD/MM/YYYY') : '-'}
        </span>
      ),
    },
    {
      key: 'returnDate',
      title: 'Return Date',
      align: 'center',
      width: 'w-32',
      render: (value: string) => (
        <span className="text-[14px]">
          {value ? dayjs(value).format('DD/MM/YYYY') : '-'}
        </span>
      ),
    },
    {
      key: 'returnDuration',
      title: 'Duration',
      align: 'center',
      width: 'w-24',
      render: (value: string) => {
        const numValue = parseInt(value) || 0;
        const dayText = numValue <= 1 ? 'Day' : 'Days';
        return <span className="text-[14px]">{numValue + ' ' + dayText}</span>;
      },
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (value: HandoverStatus) => (
        <Status value={statusLabelMapper[value] || value} type="handover" />
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-16',
      align: 'center',
      sticky: 'right',
      render: (_value: any, record: Handover, index: number) => {
        const uniqueId = record.ID || `temp-${index}`;
        const actions: ActionDropdownAction[] = [
          {
            key: 'detail',
            label: 'Detail',
            icon: 'ri-eye-line',
            onClick: () => {
              setActionDropdownOpen(null);
              navigate(`/handover/${record.ID}`);
            },
          },
          // A UUID is too long to read off a column but it is what ops quote in
          // tickets, so it lives here — no width, and it arrives whole.
          {
            key: 'copy-id',
            label: 'Copy Handover ID',
            icon: 'ri-file-copy-line',
            onClick: async () => {
              setActionDropdownOpen(null);
              if (await copyToClipboard(record.ID)) {
                showSuccessToast('Handover ID disalin', record.ID);
                return;
              }
              showErrorToast(
                'Gagal menyalin',
                'Handover ID tidak bisa disalin'
              );
            },
          },
        ];
        if (actions.length === 0) return null;
        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={actions}
            ariaLabel={`Actions for handover ${record.platNomor}`}
          />
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<Handover>({
    storageKey: 'handovers-history',
    columns,
  });

  return (
    <>
      {/* Controls */}
      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center">
            <DateRangeFilter
              key={controlsKey}
              {...datePickerSeed}
              onDateRangeChange={(range) => {
                setDateRange(range);
              }}
            />
          </div>

          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search..."
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={activeFilters}
              onApply={(filters: Record<string, any>) => {
                setFilters(filters);
              }}
              onClear={() => {
                setFilters({});
              }}
            />
            <ResetFiltersButton
              visible={hasFilters()}
              onReset={() => resetFilters()}
            />

            <ColumnSettings {...columnSettingsProps} />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <TableData
          data={sortedHandoverData}
          columns={visibleColumns}
          loading={isLoading}
          sorting={{
            field: sortField,
            direction: sortDirection,
            onSort: setSort,
          }}
          onRowClick={(record) => navigate(`/handover/${record.ID}`)}
          size="small"
          className="min-w-full"
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          fetchHandovers(1, searchTerm, activeFilters, newPageSize);
        }}
      />
    </>
  );
};

export default HistoryHandoverTab;
